import React, { useMemo } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, HandHelping, Users, Headphones, Star, Flame, Bell, Mail,
  Megaphone, Settings as SettingsIcon, FileText, LogOut, ExternalLink,
} from "lucide-react";
import { useAuth } from "../../lib/auth";

const PRIMARY_NAV = [
  { to: "/admin/dashboard", label: "Overview", icon: LayoutDashboard, testid: "admin-nav-overview" },
  { to: "/admin/donations", label: "Donations", icon: HandHelping, testid: "admin-nav-donations", founderOnly: true },
  { to: "/admin/donors", label: "Donors", icon: Users, testid: "admin-nav-donors", founderOnly: true },
  { to: "/admin/media-library", label: "Media Library", icon: Headphones, testid: "admin-nav-media" },
  { to: "/admin/latest-services", label: "Latest Services", icon: Star, testid: "admin-nav-latest" },
  { to: "/admin/prayer-requests", label: "Prayer Requests", icon: Flame, testid: "admin-nav-prayer" },
  { to: "/admin/members", label: "Members", icon: Users, testid: "admin-nav-members" },
  { to: "/admin/onboarding-drips", label: "Onboarding", icon: Mail, testid: "admin-nav-onboarding" },
  { to: "/admin/announcements", label: "Announcements", icon: Megaphone, testid: "admin-nav-announcements" },
  { to: "/admin/push", label: "Push", icon: Bell, testid: "admin-nav-push" },
];
const SECONDARY_NAV = [
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, testid: "admin-nav-settings", founderOnly: true },
  { to: "/admin/logs", label: "Activity Logs", icon: FileText, testid: "admin-nav-logs" },
];
const MOBILE_NAV = [
  { to: "/admin/dashboard", label: "Home", icon: LayoutDashboard },
  { to: "/admin/donations", label: "Giving", icon: HandHelping },
  { to: "/admin/media-library", label: "Media", icon: Headphones },
  { to: "/admin/prayer-requests", label: "Prayer", icon: Flame },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
];

const SideLink = ({ to, label, icon: Icon, testid }) => (
  <NavLink
    to={to}
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all ${
        isActive
          ? "bg-[#C9A44C]/12 text-[#F6F0E6] border border-[#C9A44C]/30"
          : "text-[#D9D9D9] hover:text-[#F6F0E6] hover:bg-[#111]/60 border border-transparent"
      }`
    }
  >
    <Icon className="h-4 w-4" strokeWidth={1.6} />
    {label}
  </NavLink>
);

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isFounder = !!user?.is_founder;

  const primaryNav = useMemo(
    () => PRIMARY_NAV.filter((n) => !n.founderOnly || isFounder),
    [isFounder],
  );
  const secondaryNav = useMemo(
    () => SECONDARY_NAV.filter((n) => !n.founderOnly || isFounder),
    [isFounder],
  );

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#F6F0E6] flex">
      <aside className="hidden lg:flex w-64 flex-col border-r border-[#C9A44C]/15 bg-[#080808]/85 backdrop-blur-xl p-5 sticky top-0 h-screen overflow-y-auto">
        <a href="/" className="flex items-center gap-3 mb-8" data-testid="admin-home-logo">
          <span className="icon-badge !h-10 !w-10">
            <Flame className="h-5 w-5 text-[#F28C18]" strokeWidth={1.5} />
          </span>
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#C9A44C]">RFMG</div>
            <div className="font-serif-display text-xl text-[#F6F0E6] -mt-0.5">Inner Court</div>
          </div>
        </a>

        <nav className="space-y-1">
          {primaryNav.map((n) => <SideLink key={n.to} {...n} />)}
        </nav>

        <div className="my-4 border-t border-[#C9A44C]/15" />
        <nav className="space-y-1">
          {secondaryNav.map((n) => <SideLink key={n.to} {...n} />)}
        </nav>

        <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-[#D9D9D9]/70 hover:text-[#C9A44C] mt-auto pt-4 px-4 py-2">
          <ExternalLink className="h-3.5 w-3.5" /> View public site
        </a>

        <div className="border-t border-[#C9A44C]/15 pt-4 mt-2">
          <div className="flex items-center gap-3 px-2 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-9 w-9 rounded-full object-cover border border-[#C9A44C]/30" />
            ) : (
              <div className="icon-badge !h-9 !w-9"><Flame className="h-4 w-4 text-[#F28C18]" /></div>
            )}
            <div className="min-w-0">
              <div className="text-sm truncate flex items-center gap-1.5">
                {user?.name || "Admin"}
                {user?.is_founder && (
                  <span className="text-[8px] tracking-[0.18em] uppercase text-[#0a0a0a] bg-[#C9A44C] rounded-full px-1.5 py-0.5 font-semibold" data-testid="founder-badge">Founder</span>
                )}
              </div>
              <div className="text-[10px] text-[#D9D9D9]/60 truncate">{user?.email}</div>
            </div>
          </div>
          <button onClick={doLogout} className="w-full flex items-center justify-center gap-2 text-xs text-[#D9D9D9] hover:text-[#E8452C] py-2 border border-[#C9A44C]/20 rounded-xl" data-testid="admin-logout">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-[#050505]/90 backdrop-blur-xl border-b border-[#C9A44C]/15 px-4 h-14 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2">
          <span className="icon-badge !h-8 !w-8"><Flame className="h-4 w-4 text-[#F28C18]" /></span>
          <span className="font-serif-display text-lg">Inner Court</span>
        </a>
        <button onClick={doLogout} className="text-xs text-[#D9D9D9]"><LogOut className="h-4 w-4" /></button>
      </header>

      <main className="flex-1 lg:p-10 px-5 pt-20 lg:pt-10 pb-28 lg:pb-12 max-w-[1400px] mx-auto w-full">
        <Outlet />
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-[#050505]/95 backdrop-blur-xl border-t border-[#C9A44C]/20 grid grid-cols-5 z-30">
          {MOBILE_NAV.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] ${isActive ? "text-[#C9A44C]" : "text-[#D9D9D9]"}`
            }>
              <Icon className="h-4 w-4" strokeWidth={1.6} />
              {label}
            </NavLink>
          ))}
        </nav>
      </main>
    </div>
  );
};

export default AdminLayout;
